import { useCallback, useState } from 'react';
import { useStartSpotifyCanvasMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { CanvasPostOutcome, canvasPostOutcome } from './spotifyCanvasErrors';

/** The line under the buttons after a refused POST: the file rule, or the general sentence. */
export type CanvasPostError = Extract<CanvasPostOutcome, { kind: 'file' | 'generic' }>;

/**
 * Upload to Spotify, Upload it, Try again and Replace it (design spotify-canvas a2, b4): the POST
 * of the song. With no accepted terms the first click opens the terms dialog; a 409
 * M24_CANVAS_TERMS_REQUIRED opens it too. "Accept and upload" sends the POST with `acceptTerms`.
 */
export function useSpotifyCanvasUpload({
  accountId,
  catalogItemUuid,
  termsAccepted,
  refetch,
  hide,
}: {
  accountId: number;
  catalogItemUuid: string;
  termsAccepted: boolean;
  refetch: () => void;
  hide: () => void;
}) {
  const [start, { isLoading }] = useStartSpotifyCanvasMutation();
  const [termsOpen, setTermsOpen] = useState(false);
  const [error, setError] = useState<CanvasPostError | null>(null);
  const [termsFailed, setTermsFailed] = useState(false);

  const send = useCallback(
    async (acceptTerms: boolean): Promise<void> => {
      setError(null);
      setTermsFailed(false);
      const result = await start({
        accountId,
        body: acceptTerms ? { catalogItemUuid, acceptTerms: true } : { catalogItemUuid },
      });
      if (isSuccessfulResult(result)) {
        setTermsOpen(false);
        return;
      }
      const outcome = canvasPostOutcome(result.error);
      switch (outcome.kind) {
        case 'terms':
          setTermsOpen(true);
          return;
        case 'reload':
          setTermsOpen(false);
          refetch();
          return;
        case 'hide':
          setTermsOpen(false);
          hide();
          return;
        case 'file':
          setTermsOpen(false);
          setError(outcome);
          return;
        case 'generic':
          // A failed accept keeps the dialog open with the sentence (design b3 note).
          if (acceptTerms) {
            setTermsFailed(true);
          } else {
            setError(outcome);
          }
      }
    },
    [accountId, catalogItemUuid, hide, refetch, start]
  );

  const upload = useCallback(() => {
    if (!termsAccepted) {
      setError(null);
      setTermsFailed(false);
      setTermsOpen(true);
      return;
    }
    send(false).catch(console.error);
  }, [send, termsAccepted]);

  return {
    upload,
    acceptAndUpload: useCallback(() => {
      send(true).catch(console.error);
    }, [send]),
    closeTerms: useCallback(() => setTermsOpen(false), []),
    termsOpen,
    termsFailed,
    isUploading: isLoading,
    error,
  };
}
